import { useRef } from 'react';
import SuccessFormMessage from '../../../../components/messages/SuccessFormMessage';
import FormInfo from '../../../../components/messages/FormInfo';
import { SubcontractChangeOrderResult } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { useQuery } from '@tanstack/react-query';
import { useCompanyContext } from '../../../../context/CompanyContext';
import {
  fecthSCVendors,
  fetchSubcontracts,
} from '../../../../services/accounting/SubcontractsService';
import SLChangeOrderSuccessTable from './SLChangeOrderSuccessTable';
import SLChangeOrderReport from './SLChangeOrderReport';

type SLChangeOrdersSuccessProps = {
  result: SubcontractChangeOrderResult;
};

const SLChangeOrdersSuccess = ({ result }: SLChangeOrdersSuccessProps) => {
  const { selectedCompany } = useCompanyContext();
  const summaryRef = useRef<HTMLDivElement | null>(null);

  const getSubcontracts = useQuery({
    queryKey: ['getSubcontracts', selectedCompany],
    queryFn: ({ signal }) =>
      fetchSubcontracts(selectedCompany!.id, undefined, [0], signal),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const selectedSubcontract: {
    SL: string;
    job: string;
    retainage: number;
    jobDescription: string;
  } | null = getSubcontracts.data?.find(
    (subcontract: { SL: string; job: string }) => subcontract.SL === result.sl
  );

  const vendorRequest = useQuery({
    queryKey: [
      'getSubcontractVendor',
      selectedCompany,
      result.sl,
      selectedSubcontract?.job,
    ],
    queryFn: ({ signal }) => {
      return fecthSCVendors(
        {
          companyID: selectedCompany!.id,
          job: selectedSubcontract?.job ?? '',
          subcontract: result.sl,
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const vendor = (vendorRequest.data || [])[0];

  return (
    <div>
      <div className="mb-6">
        <SuccessFormMessage
          message="The Subcontract Change Orders data has been uploaded successfully!"
          redirectUrl="/accounting-dashboard/new"
          redirectMessage="Click here to return to the forms list"
        />
      </div>
      <div
        id="sl-co-success"
        className="mx-auto flex flex-column gap-3 px-3"
        ref={summaryRef}
      >
        <div className="text-30px font-bold flex justify-content-between align-items-end">
          SL Change Orders Summary:
          <div className="text-23px cursor-pointer printHide h-fit">
            <SLChangeOrderReport
              elementRef={summaryRef}
              tableID="sl-co-success-items"
              isTextButton={true}
            />
          </div>
        </div>
        <div className="grid w-full">
          <div className="col-3 ml-3">
            <FormInfo label="Subcontract #" content={result.sl} />
          </div>
          <div className="col-3">
            <FormInfo
              label="Job"
              content={`${selectedSubcontract?.job} - ${selectedSubcontract?.jobDescription}`}
            />
          </div>
          <div className="col-3">
            <FormInfo
              label="Vendor"
              content={`${vendor.Vendor} - ${vendor.Name}`}
            />
          </div>
          <div className="col-12">
            <SLChangeOrderSuccessTable
              items={[
                ...(result?.editItems?.map((item) => ({
                  ...item,
                  isNew: false,
                })) ?? []),
                ...(result?.newItems?.map((item) => ({
                  ...item,
                  isNew: true,
                })) ?? []),
              ]}
              job={selectedSubcontract?.job ?? ''}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLChangeOrdersSuccess;
